"use client";
import React, { useState } from 'react';
import Image from 'next/image';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Search, 
  Menu, 
  X,
  Volume2,
  ChevronDown,
  PhoneCall,
  Mail,
  MessageSquare
} from 'lucide-react';

export default function Navbar() {
  const [isOpen, setIsOpen] = useState(false);
  
  // حالات التحكم في فتح المنيو المنسدل (للديسك توب والموبايل)
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [isMobileDropdownOpen, setIsMobileDropdownOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");

  // حالة التحكم في نافذة تواصل معنا (Popup)
  const [isContactPopupOpen, setIsContactPopupOpen] = useState(false);

  // 1. مصفوفة الروابط الأساسية في الجزء السفلي (Main Navigation Links)
  const navLinks = [
    {
      id: "mini-money",
      label: "ميني ماني - Mini Money",
      href: "#mini-money",
      type: "badge", // ستايل مميز أخضر
      badgeColor: "bg-emerald-50 text-emerald-700 border-emerald-100/70 hover:bg-emerald-100/80 hover:text-emerald-800",
      pingColor: "bg-emerald-500",
      mobileBg: "bg-emerald-50 text-emerald-700 border-emerald-100/60",
      icon: "✨"
    },
    {
      id: "laser-jet",
      label: "ليزر جيت - LaserJet",
      href: "#laser-jet-e-shop",
      type: "badge", // ستايل مميز أزرق
      badgeColor: "bg-blue-50 text-[#004aad] border-blue-100/70 hover:bg-blue-100/80 hover:text-blue-900",
      pingColor: "bg-[#004aad]",
      mobileBg: "bg-blue-50 text-[#004aad] border-blue-100/60",
      icon: "⚡"
    },
  
    {
      id: "laser-host",
      label: "ليزر هوست - Laserjet Host",
      href: "#LaserJet-Host",
      type: "badge", // ستايل مميز بنفسجي
      badgeColor: "bg-purple-50 text-purple-700 border-purple-100/70 hover:bg-purple-100/80 hover:text-purple-800",
      pingColor: "bg-purple-500",
      mobileBg: "bg-purple-50 text-purple-700 border-purple-100/60",
      icon: "🌐"
    },
      {
      id: "blogs",
      label: "المدونة - Blog",
      href: "/blogs",
      type: "badge",
      badgeColor: "bg-blue-50 text-[#004aad] border-blue-100/70 hover:bg-blue-100/80 hover:text-blue-900",
      pingColor: "bg-[#004aad]",
      mobileBg: "bg-blue-50 text-[#004aad] border-blue-100/60",
      icon: "⚡"
    },
    {
      id: "dropdown-placeholder",
      type: "dropdown" // علامة مميزة لتحديد مكان القائمة المنسدلة للخدمات الأخرى أثناء التكرار
    },
    {
      id: "sell-with-us",
      label: "بيع معانا - Sell With Us",
      href: "#offers",
      type: "normal", // ستايل عادي بخط سفلي متحرك
    }
  ];

  // روابط القائمة المنسدلة الخاصة بـ "خدمات اخرى - Offers"
  const otherServicesItems = [
    { label: "وكلاء ليزرجيت مصر (LaserJet Agents)", href: "#other-services" },
    { label: "شركاء ليزرجيت (LaserJet Partners)", href: "#other-services" },
    { label: " منصة التاجر الرقم (Sell)", href: "#other-services" },
    { label: "ليزرجيت سيرفيس (LaserJet Service)", href: "#other-services" },
  ];

  // داتا الأخبار الاستاتيك الخاصة بخدمات ليزر جيت
  const newsItems = [
    "🤝 انضموا لنا: ليزرجيت التجار.. بوابتكم لتطوير وتوسيع نطاق أعمالكم.",
    "🚀 شراكات استراتيجية: ليزرجيت الشركاء.. معاً نبني مستقبلاً أقوى في سوق الإلكترونيات.",
    "🌐 استضافة وحلول: اكتشف خدمات \"ليزرجيت هوست\" المخصصة لدعم أعمالك الرقمية.",
    "💎 تكامل الأعمال: من التجارة إلى الاستضافة، ليزرجيت هي شريكك المتكامل في مصر.",
    "📞 دعم فني وتجاري: اتصل بـ 01004331113 للاستفسار عن خدمات التجار والشركاء."
  ];

  // دالة البحث الذكي للتوجه للقسم المطلوب
  const handleSearch = (e) => {
    e.preventDefault();
    if (!searchQuery.trim()) return;
    
    const query = searchQuery.toLowerCase();
    
    const allSearchableLinks = [
      ...navLinks.filter(l => l.label).map(l => ({ label: l.label, href: l.href })),
      ...otherServicesItems,
      { label: "قسم المساعدة help section", href: "#devices" },
      { label: "عروض حصرية", href: "#offers" },
    ];
    
    const foundLink = allSearchableLinks.find(link => 
      link.label.toLowerCase().includes(query)
    );
    
    if (foundLink && foundLink.href && foundLink.href !== "#") {
      const targetElement = document.querySelector(foundLink.href);
      if (targetElement) {
        targetElement.scrollIntoView({ behavior: 'smooth' });
      } else {
        window.location.href = foundLink.href;
      }
      setSearchQuery("");
    } else {
      alert("عفواً، لم يتم العثور على نتيجة لـ: " + searchQuery);
    }
  };

  return (
    <header className="w-full font-sans text-right bg-white relative z-50" dir="rtl">
      {/* 1. التوب بار (Top Bar) المطور بشريط الأخبار التفاعلي */}
      <div className="bg-[#004aad] text-white py-2 px-4 md:px-8 flex flex-col md:flex-row justify-between items-center gap-4 border-b border-white/10 overflow-hidden">
        
        {/* جهة اليمين والوسط - تشمل شريط الأخبار ورابط تواصل معنا المطور بجانبه */}
        <div className="w-full md:w-auto flex flex-col sm:flex-row items-center gap-4 lg:gap-6 flex-1">
          
          {/* شريط الأخبار المتحرك واللا نهائي */}
          <div className="w-full sm:w-[280px] md:w-[320px] lg:w-[500px] xl:w-[580px] flex items-center bg-white/5 rounded-lg px-3 py-1 overflow-hidden border border-white/5 relative">
            <div className="flex items-center gap-1.5 bg-[#1823bd] text-white px-2.5 py-1 rounded-md text-[11px] font-black z-10 shadow-md ml-3 whitespace-nowrap">
              <Volume2 className="w-3.5 h-3.5 text-white animate-pulse" />
              <span>آخر الأخبار</span>
            </div>

            <div className="w-full overflow-hidden flex items-center relative h-5">
              <motion.div 
                className="flex items-center gap-16 whitespace-nowrap pl-4" 
                animate={{ x: ["-100%", "100%"] }} 
                transition={{
                  ease: "linear",
                  duration: 45, 
                  repeat: Infinity,
                }}
                whileHover={{ animationPlayState: "paused" }}
              >
                {newsItems.map((news, idx) => (
                  <span key={idx} className="text-[12px] font-bold text-white/90 hover:text-[#b0b1ba] cursor-pointer transition-colors flex items-center gap-2">
                    {news}
                  </span>
                ))}
              </motion.div>
            </div>
          </div>
        </div>

        {/* أقصى جهة اليسار - محرك البحث منفصل بشكل منظم */}
        <div className="flex items-center justify-center md:justify-end w-full md:w-auto">
          <form 
            onSubmit={handleSearch}
            className="relative w-full min-w-[220px] max-w-[260px]"
          >
            <input 
              type="text" 
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="إبحث عن باقة أو خدمة..." 
              className="peer w-full bg-white/10 text-white placeholder-white/50 text-xs rounded-lg pr-9 pl-3 py-1.5 focus:outline-none focus:bg-white focus:text-gray-900 focus:placeholder-gray-400 transition-all border border-white/10 focus:border-white text-right"
            />
            <button type="submit" className="absolute right-3 top-2 focus:outline-none cursor-pointer">
              <Search className="w-3.5 h-3.5 text-white/50 peer-focus:text-gray-400 hover:text-white peer-focus:hover:text-gray-600 transition-colors" />
            </button>
          </form>
        </div>

      </div>

      {/* 2. المين بار الرئيسي (Main Navbar) */}
      <div className="border-b border-gray-100 py-3 px-4 flex justify-between items-center relative bg-white">
        <div className="w-full max-w-7xl mx-auto flex justify-between items-center gap-4">
          
          {/* اللوجو */}
     <div className="flex items-center cursor-pointer group w-[240px] sm:w-[260px] h-[80px] relative py-1 overflow-visible">
  <Image
    src="/logo55.png"
    alt="LASERJET Telecom"
    width={260}
    height={160}
    className="w-full h-full group-hover:opacity-80 transition-all duration-300 object-contain"
    priority
  />
</div>

          {/* روابط التنقل الأساسية للشاشات الكبيرة */}
          <div className="hidden lg:flex items-center gap-x-4 xl:gap-x-6">
            <nav className="flex items-center gap-x-3 xl:gap-x-4 text-[13px] font-bold text-gray-600">
              {navLinks.map((link) => {
                if (link.type === "badge") {
                  return (
                    <a 
                      key={link.id}
                      href={link.href} 
                      className={`relative px-2.5 py-1.5 rounded-full border transition-all duration-200 block whitespace-nowrap flex items-center gap-1 shadow-sm ${link.badgeColor}`}
                    >
                      <span className={`w-1.5 h-1.5 rounded-full animate-ping ${link.pingColor}`} />
                      <span>{link.label}</span>
                    </a>
                  );
                }

                if (link.type === "dropdown") {
                  return (
                    <div 
                      key="desktop-dropdown"
                      className="relative py-2.5 cursor-pointer"
                      onMouseEnter={() => setIsDropdownOpen(true)}
                      onMouseLeave={() => setIsDropdownOpen(false)}
                    >
                      <button className="flex items-center gap-1 hover:text-[#004aad] transition-colors duration-200 font-bold focus:outline-none whitespace-nowrap text-gray-600">
                        <span>خدمات اخرى - Other services</span>
                        <motion.div animate={{ rotate: isDropdownOpen ? 180 : 0 }} transition={{ duration: 0.2 }}>
                          <ChevronDown className="w-3.5 h-3.5" />
                        </motion.div>
                      </button>

                      <AnimatePresence>
                        {isDropdownOpen && (
                          <motion.div 
                            initial={{ opacity: 0, y: 15 }}
                            animate={{ opacity: 1, y: 0 }}
                            exit={{ opacity: 0, y: 15 }}
                            transition={{ duration: 0.2, ease: "easeOut" }}
                            className="absolute right-0 top-full mt-1 w-64 bg-white border border-gray-100 rounded-xl shadow-xl py-2 z-50 text-right"
                          >
                            {otherServicesItems.map((subItem, subIdx) => (
                              <a
                                key={subIdx}
                                href={subItem.href}
                                className="block px-4 py-2.5 text-sm text-gray-700 hover:bg-slate-50 hover:text-[#004aad] transition-colors duration-150 font-semibold"
                              >
                                {subItem.label}
                              </a>
                            ))}
                          </motion.div>
                        )}
                      </AnimatePresence>
                    </div>
                  );
                }

                return (
                  <a 
                    key={link.id}
                    href={link.href} 
                    className="relative py-2.5 hover:text-[#004aad] transition-colors duration-200 block group whitespace-nowrap"
                  >
                    <span>{link.label}</span>
                    <span className="absolute bottom-0 right-0 w-full h-0.5 bg-[#004aad] transform scale-x-0 origin-right group-hover:scale-x-100 transition-transform duration-300 ease-out" />
                  </a>
                );
              })}
            </nav>

            {/* خط فاصل أنيق قبل زرار تواصل معنا للـ Desktop */}
            <span className="h-5 w-px bg-gray-200" />

            {/* تم ربط الزر ليقوم بفتح الـ Popup عند الضغط */}
            <button 
              onClick={() => setIsContactPopupOpen(true)}
              className="flex items-center gap-2 px-4 py-2 text-[12px] font-black text-white bg-[#004aad] hover:bg-[#003680] rounded-xl transition-all duration-200 shadow-sm shadow-[#004aad]/10 whitespace-nowrap group focus:outline-none cursor-pointer"
            >
              <PhoneCall className="w-3.5 h-3.5 text-white/90 group-hover:rotate-12 transition-transform" />
              <span>تواصل معنا </span>
            </button>
          </div>

          {/* زر الموبايل منيو */}
          <button 
            onClick={() => setIsOpen(!isOpen)} 
            className="lg:hidden text-gray-600 hover:text-[#004aad] p-2 hover:bg-gray-50 rounded-lg transition-colors focus:outline-none"
          >
            {isOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>

        {/* المنيو المتكامل للموبايل والتابلت */}
        <AnimatePresence>
          {isOpen && (
            <motion.div 
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -10 }}
              className="absolute top-full right-0 w-full bg-white border-t border-gray-100 shadow-xl z-40 flex flex-col p-4 space-y-2.5 lg:hidden text-right text-base font-bold"
            >
              {navLinks.map((link) => {
                if (link.type === "badge") {
                  return (
                    <a 
                      key={`mobile-${link.id}`}
                      href={link.href} 
                      onClick={() => setIsOpen(false)}
                      className={`${link.mobileBg} py-3 px-3 rounded-xl block transition-colors border`}
                    >
                      {link.label} {link.icon}
                    </a>
                  );
                }

                if (link.type === "dropdown") {
                  return (
                    <div key="mobile-dropdown" className="border border-gray-100 rounded-xl overflow-hidden">
                      <button 
                        onClick={() => setIsMobileDropdownOpen(!isMobileDropdownOpen)}
                        className="w-full text-gray-700 hover:bg-gray-50 py-3 px-3 flex items-center justify-between transition-colors font-bold focus:outline-none"
                      >
                        <span>خدمات اخرى - Offers</span>
                        <motion.div animate={{ rotate: isMobileDropdownOpen ? 180 : 0 }}>
                          <ChevronDown className="w-4 h-4 text-gray-400" />
                        </motion.div>
                      </button>
                      
                      <AnimatePresence>
                        {isMobileDropdownOpen && (
                          <motion.div 
                            initial={{ height: 0, opacity: 0 }}
                            animate={{ height: "auto", opacity: 1 }}
                            exit={{ height: 0, opacity: 0 }}
                            className="bg-slate-50/70 overflow-hidden pr-2"
                          >
                            {otherServicesItems.map((subItem, subIdx) => (
                              <a 
                                key={subIdx} 
                                href={subItem.href}
                                onClick={() => { setIsOpen(false); setIsMobileDropdownOpen(false); }}
                                className="text-gray-600 hover:text-[#004aad] py-2.5 px-3 block text-sm font-semibold border-r-2 border-gray-200"
                              >
                                {subItem.label}
                              </a>
                            ))}
                          </motion.div>
                        )}
                      </AnimatePresence>
                    </div>
                  );
                }

                return (
                  <a 
                    key={`mobile-${link.id}`}
                    href={link.href} 
                    onClick={() => setIsOpen(false)}
                    className="text-gray-700 hover:bg-gray-50 py-3 px-3 rounded-xl block transition-colors border-b border-gray-100"
                  >
                    {link.label}
                  </a>
                );
              })}

              {/* زر تواصل معنا مدمج داخل منيو الموبايل ومربوط بالبوب اب */}
              <div className="grid grid-cols-2 gap-2 pt-2 px-2">
                <button 
                  onClick={() => { setIsOpen(false); setIsContactPopupOpen(true); }}
                  className="text-[#004aad] bg-blue-50 hover:bg-blue-100 text-center py-3 rounded-xl flex items-center justify-center gap-2 text-sm font-black transition-all border border-blue-100 focus:outline-none cursor-pointer"
                >
                  <PhoneCall className="w-4 h-4" />
                  تواصل معنا
                </button>

                <a 
                  href="#" 
                  onClick={() => setIsOpen(false)}
                  className="text-white bg-[#004aad] hover:bg-[#004aad]/90 text-center py-3 rounded-xl flex items-center justify-center text-sm font-black shadow-md shadow-[#004aad]/10 transition-all"
                >
                  LaserJet Pay
                </a>
              </div>
              
            </motion.div>
          )}
        </AnimatePresence>
      </div>

      {/* ─── بوب اب تواصل معنا (Contact Us Popup Modal) ─── */}
      <AnimatePresence>
        {isContactPopupOpen && (
          <div className="fixed inset-0 z-[100] flex items-center justify-center p-4">
            
            {/* الخلفية المظلمة والضبابية */}
            <motion.div 
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={() => setIsContactPopupOpen(false)}
              className="absolute inset-0 bg-slate-900/40 backdrop-blur-sm"
            />

            {/* جسم النافذة المنبثقة (Popup Container) */}
            <motion.div 
              initial={{ opacity: 0, scale: 0.9, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.9, y: 20 }}
              transition={{ type: "spring", duration: 0.4 }}
              className="bg-white w-full max-w-md rounded-2xl shadow-2xl border border-slate-100 p-6 relative z-10 text-right overflow-hidden"
            >
              {/* زر الإغلاق العلوي */}
              <button 
                onClick={() => setIsContactPopupOpen(false)}
                className="absolute top-4 left-4 p-2 text-gray-400 hover:text-gray-600 hover:bg-slate-50 rounded-xl transition-all focus:outline-none"
              >
                <X className="w-5 h-5" />
              </button>

              {/* رأس البوب اب */}
              <div className="flex items-center gap-3 mb-6 border-b border-slate-100 pb-4">
                <div className="w-10 h-10 rounded-xl bg-blue-50 flex items-center justify-center text-[#004aad]">
                  <PhoneCall className="w-5 h-5" />
                </div>
                <div>
                  <h3 className="text-lg font-black text-gray-900">مركز خدمة العملاء والتواصل</h3>
                  <p className="text-xs text-gray-400 font-medium mt-0.5">يسعدنا دائماً استقبال استفساراتكم على مدار الساعة</p>
                </div>
              </div>

              {/* قنوات الاتصال المتاحة */}
              <div className="space-y-3">
                
                {/* الخط الساخن */}
                <a 
                  href="tel:01004331113" 
                  className="flex items-center justify-between p-3.5 rounded-xl border border-slate-100 hover:border-[#004aad]/30 bg-slate-50/50 hover:bg-white transition-all group"
                >
                  <div className="flex items-center gap-3">
                    <div className="w-9 h-9 rounded-lg bg-blue-50 text-[#004aad] flex items-center justify-center group-hover:bg-[#004aad] group-hover:text-white transition-colors">
                      <PhoneCall className="w-4 h-4" />
                    </div>
                    <div>
                      <p className="text-xs text-gray-400 font-medium mb-0.5">الرقم المباشر والخط الساخن</p>
                      <p className="text-sm font-bold text-gray-800 tracking-wide font-mono">01004331113</p>
                    </div>
                  </div>
                  <span className="text-xs font-bold text-[#004aad] bg-blue-50 px-2.5 py-1 rounded-md">اتصل الآن</span>
                </a>

                {/* واتساب المباشر */}
                <a 
                  href="https://whatsapp.com/channel/0029Va23H37GufIsQw29xG0a" 
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center justify-between p-3.5 rounded-xl border border-slate-100 hover:border-emerald-500/30 bg-slate-50/50 hover:bg-white transition-all group"
                >
                  <div className="flex items-center gap-3">
                    <div className="w-9 h-9 rounded-lg bg-emerald-50 text-emerald-600 flex items-center justify-center group-hover:bg-emerald-600 group-hover:text-white transition-colors">
                      <MessageSquare className="w-4 h-4" />
                    </div>
                    <div>
                      <p className="text-xs text-gray-400 font-medium mb-0.5">المحادثة الفورية عبر واتساب</p>
                      <p className="text-sm font-bold text-gray-800">دعم ليزرجيت السريع</p>
                    </div>
                  </div>
                  <span className="text-xs font-bold text-emerald-600 bg-emerald-50 px-2.5 py-1 rounded-md">دردشة</span>
                </a>

                {/* البريد الإلكتروني */}
                <a 
                  href="info@laserjet.com.eg" 
                  className="flex items-center justify-between p-3.5 rounded-xl border border-slate-100 hover:border-purple-500/30 bg-slate-50/50 hover:bg-white transition-all group"
                >
                  <div className="flex items-center gap-3">
                    <div className="w-9 h-9 rounded-lg bg-purple-50 text-purple-600 flex items-center justify-center group-hover:bg-purple-600 group-hover:text-white transition-colors">
                      <Mail className="w-4 h-4" />
                    </div>
                    <div>
                      <p className="text-xs text-gray-400 font-medium mb-0.5">الدعم الفني والمراسلات</p>
                      <p className="text-sm font-bold text-gray-800 font-mono text-left">minimoney@laserjet.com.eg</p>
                    </div>
                  </div>
                  <span className="text-xs font-bold text-purple-600 bg-purple-50 px-2.5 py-1 rounded-md">إرسال</span>
                </a>

              </div>

              {/* أسفل النافذة */}
              <div className="mt-5 text-center bg-slate-50 -mx-6 -mb-6 p-4 border-t border-slate-100">
                <button 
                  onClick={() => setIsContactPopupOpen(false)}
                  className="text-xs font-bold text-gray-500 hover:text-gray-800 transition-colors focus:outline-none"
                >
                  إغلاق النافذة
                </button>
              </div>

            </motion.div>
          </div>
        )}
      </AnimatePresence>

    </header>
  );
}